import {clientQuery} from 'src/redux/clientQuery';
import {normalizeApiError} from 'src/redux/normalizeApiError';
import {getActiveSession} from './offlineSessionManager';
import {enqueue} from './outbox';
import type {EnqueueApplyLocalContext} from './outbox';

export interface OfflineQueuedResult {
  offlineQueued: true;
  clientSubmissionId: string;
}

export interface OfflineMutationOptions<Args> {
  /** Diverts to the per-context bound-and-ready session, regardless of
   * connectivity (pinned). Omit for legacy connectivity-agnostic diversion. */
  contextKey?: (args: Args) => string;
  /** Decides diversion once the session is ready (evaluated lazily, only then).
   * Omit to always divert to a ready session; return false to stay on network. */
  shouldDivert?: (args: Args) => boolean;
  /** Local write run atomically with the enqueue (same Dexie transaction). */
  applyLocal?: (args: Args, ctx: EnqueueApplyLocalContext) => Promise<void>;
}

export const isOfflineQueuedResult = (
  value: unknown
): value is OfflineQueuedResult =>
  typeof value === 'object' &&
  value !== null &&
  (value as {offlineQueued?: unknown}).offlineQueued === true;

/**
 * `clientQuery`-compatible wrapper. Context-scoped kinds divert to a bound
 * ready session; legacy kinds divert to the current instance/user session.
 */
export const offlineMutation =
  <Args, Result>(
    kind: string,
    call: (args: Args) => Promise<Result>,
    options?: OfflineMutationOptions<Args>
  ) =>
  async (args: Args): Promise<{data: Result} | {error: unknown}> => {
    const contextKey = options?.contextKey?.(args);
    // getActiveSession already scopes to the signed-in user; only readiness left.
    const session = await getActiveSession(contextKey);
    const shouldDivert =
      !!session?.ready &&
      (options?.shouldDivert ? options.shouldDivert(args) : true);

    if (shouldDivert) {
      try {
        const item = await enqueue({
          kind,
          payload: args,
          contextKey,
          applyLocal: options?.applyLocal
            ? ctx => options.applyLocal!(args, ctx)
            : undefined,
        });
        const queued: OfflineQueuedResult = {
          offlineQueued: true,
          clientSubmissionId: item.clientSubmissionId,
        };
        return {data: queued as unknown as Result};
      } catch (error) {
        return {error: normalizeApiError(error)};
      }
    }

    return clientQuery(call)(args);
  };
